import _ from 'lodash';
import ActionButton from 'components/ActionButton';
import ActionCheckbox from 'components/ActionCheckBox';
import BackButton from 'components/BackButton';
import JobServerInstanceDropdown from 'components/JobServerInstanceDropdown';
import { Loading } from 'components/loading/Loading';
import RenderInfo from 'components/RenderInfo';
import React, { useEffect, useState } from 'react';
import { fetchCronJobDetails, setJobCronExpression, setJobParams, setJobServerInstance, toggleJobActiveStatus } from 'services/api/cronJobs';
import { DATE_FORMATS, formatDate } from 'utils';
import { showError, showSuccess } from 'utils/notifications';
import styles from './index.module.scss';

const CronJobDetails = props => {
  const [jobDetails, setJobDetails] = useState<Record<string, any>>({});
  const [params,setParams] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [activeLoading, setActiveLoading] = useState<boolean>(false);
  const [editJob, setEditJob] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const { jobId } = props.match.params;

  const fetchCronJobDetailsFn = async () => {
    try {
      const response = await fetchCronJobDetails(jobId);
      setJobDetails(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCronJobDetailsFn();
  }, []);

  const toggleCronJobStatus = async (active: boolean) => {
    setActiveLoading(true);
    try {
      await toggleJobActiveStatus(jobId);
      setJobDetails({ ...jobDetails, active });
      showSuccess(`${jobDetails.name} marked ${!active ? 'INACTIVE' : 'ACTIVE'} successfully`);
    } catch (e) {
      showError(e);
    } finally {
      setActiveLoading(false);
    }
  };

  const getParams = ()=>{
    try {
      const paramsJsoned = JSON.parse(params);
      return paramsJsoned;
    } catch (e){
      throw e.message;
    } 
  };

  const updateServerInstance = async () => {
    setEditLoading(true);
    try {
      if (jobDetails.jobServerInstance) {
        await setJobServerInstance(jobId, jobDetails.jobServerInstance);
      }
      if (jobDetails.cronExpression) {
        await setJobCronExpression(jobId, jobDetails.cronExpression);
      }
      if (!_.isEmpty(params)) {
        const paramsToUpdate = getParams();
        await setJobParams(jobId, paramsToUpdate);
      }
      setEditJob(false);
      await fetchCronJobDetailsFn();
      showSuccess('Job updated successfully');
    } catch (e) {
      showError(e);
    } finally {
      setEditLoading(false);
    }
  };

  const getRightPanel = () => {
    const cronJobHealthStatus = jobDetails?.error ? 'UNHEALTHY' : 'HEALTHY';
    return (
      <div>
        {getEditButton()}
        <div className={styles['details-group']}>
          <div className={styles['active-input-container']}>
            <ActionCheckbox
              checked={jobDetails.active}
              disabled={activeLoading}
              onChange={toggleCronJobStatus}
            />
            <div className={styles['active-label']}>Active</div>
          </div>
        </div>
        <RenderInfo heading='Created At' value={jobDetails.createdAt ? formatDate(jobDetails.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-'} />
        <RenderInfo heading='Last Run Time' value={jobDetails.lastRunTime ? formatDate(jobDetails.lastRunTime?.iso, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-'} />
        <RenderInfo heading='Last Error Time' value={jobDetails.lastErrorTime ? formatDate(jobDetails.lastErrorTime?.iso, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-'} />
        <div className={styles['details-group']}>
          <div className={`${styles['status']} ${styles[cronJobHealthStatus]}`}>
            {cronJobHealthStatus}
          </div>
        </div>
      </div>
    );
  };

  const handleCronExpChange = (value: string) => {
    setJobDetails({ ...jobDetails, cronExpression: value });
  };

  const getJobParams = () => {
    return (
      <div className={'details-group'}>
        <div className={'label'}>CRON Job Params</div>
        {editJob ? (
          <textarea
            className={'form-control'}
            defaultValue={JSON.stringify(jobDetails.params)}
            disabled={!editJob}
            onChange={e => setParams( e.target.value )}
          />
        ) :
          (
            <div className={styles['detail']}>{JSON.stringify(jobDetails.params) || '-'}</div>
          )}
      </div>
    );
  };

  const getLeftPanel = () => (
    <div >
      <RenderInfo heading='Job ID' value={jobId} />
      <RenderInfo heading='Job Name' value={jobDetails.name || '-'} />
      <div className={styles['details-group']}>
        <div className={styles['label']}>Job Server Instance</div>
        {editJob ? (
          <JobServerInstanceDropdown
            value={jobDetails.jobServerInstance}
            disabled={editLoading}
            onChange={jobServerInstance => setJobDetails({ ...jobDetails, jobServerInstance })}
          />
        ) : (
          <div className={styles['detail']}>{jobDetails.jobServerInstance || '-'}</div>
        )}
      </div>

      <div className={styles['details-group']}>
        <div className={styles['label']}>CRON Expression</div>
        {editJob ? (
          <input
            className={'form-control'}
            value={jobDetails.cronExpression}
            disabled={!editJob}
            onChange={e => handleCronExpChange(e.target.value)}
          />
        ) :
          (
            <div className={styles['detail']}>{jobDetails.cronExpression || '-'}</div>
          )}
      </div>
      <RenderInfo heading='CRON Expression String' value={jobDetails.cronExpressionStr || '-'} />
      {getJobParams()}
      <RenderInfo heading='Error' value={jobDetails.error || '-'} />
    </div>
  );

  const getEditButton = () => {
    if (!editJob) {
      return (
        <ActionButton
          text={'Edit'}
          active={true}
          isLoading={false}
          onClick={() => setEditJob(true)}
        />
      );
    }
    else {
      return (
        <div className={styles['buttons-container']}>
          <ActionButton
            text={'Save'}
            active={!editLoading}
            isLoading={editLoading}
            onClick={updateServerInstance}
          />
          <ActionButton
            text={'Cancel'}
            active={!editLoading}
            isLoading={editLoading}
            onClick={() => { setEditJob(false) }}
          />
        </div>
      );
    }
  };

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>CRON Job Details</div>
      <BackButton />
    </div>
  );

  const getCronJobDetails = () => (
    <div className={styles['details-section']}>
      {getLeftPanel()}
      {getRightPanel()}
    </div>
  );

  if (loading) return <Loading />;
  return (
    <div>
      {getHeader()}
      {getCronJobDetails()}
    </div>
  );
};

export default CronJobDetails;